import type { CriteriaColor } from '@/components/Conversations/Application/ScoreChip';
import type { InterviewInitiationSource } from '@/core/shared/interview-scheduling/types/interview-scheduling-event.type';
import type { InterviewType } from '@/core/shared/interview-scheduling/types/interview-scheduling.type';

export interface ConversationMessage {
  id: number;
  /** Namespaced identity for deduplication and rendering in the combined timeline. */
  key: string;
  conversationId: number;
  content: string;
  createdAt: Date;
  avatarUrl?: string;
  sender?: {
    id?: number | null;
    name: string;
  };
  metadata?:
    | ScoreBlurbMetadata
    | ApplicationStatusMetadata
    | InterviewSchedulingMetadata
    | DoNotContactMetadata;
}

export interface ConversationMessagesResponse {
  messages: ConversationMessage[];
}

export enum ConversationMessageType {
  SENT_BY_BOT = 'SENT_BY_BOT',
  SENT_BY_PRO = 'SENT_BY_PRO',
  SENT_BY_EMPLOYER = 'SENT_BY_EMPLOYER',
}

export interface NewMessageDto {
  message: string;
}

export interface CreatedMessage {
  id: number;
  conversationId: number;
  senderId: number;
  content: string;
  createTs: Date;
  updateTs: Date;
}

export interface GetMessagePagination {
  page?: number;
  itemsPerPage?: number;
}

export type GetMessagesByConversationResponse = {
  messages: MessagesByConversation[];
};

export type MessagesByConversation = {
  id: number;
  content: string;
  createTs: Date;
  sender: {
    senderId: number | null;
    name?: string;
  };
};

export enum MessageMetadataType {
  DO_NOT_CONTACT = 'DO_NOT_CONTACT',
  SCORE_BLURB = 'SCORE_BLURB',
  APPLICATION_STATUS = 'APPLICATION_STATUS',
  INTERVIEW_SCHEDULING_INITIATED = 'INTERVIEW_SCHEDULING_INITIATED',
  INTERVIEW_SCHEDULED = 'INTERVIEW_SCHEDULED',
}

export interface DoNotContactMetadata {
  type: MessageMetadataType.DO_NOT_CONTACT;
  action: 'marked' | 'removed';
}

export interface ScoreBlurbMetadata {
  type: MessageMetadataType.SCORE_BLURB;
  score: string;
  chips?: {
    title: string;
    text: string;
    color: CriteriaColor;
  }[];
  applicationId: number;
  scoreVersion?: string;
  jobTitle: string;
}

export interface ApplicationStatusMetadata {
  type: MessageMetadataType.APPLICATION_STATUS;
}

export interface InterviewSchedulingMetadata {
  type:
    | MessageMetadataType.INTERVIEW_SCHEDULING_INITIATED
    | MessageMetadataType.INTERVIEW_SCHEDULED;
  threadId: number;
  initiationSource: InterviewInitiationSource;
  recruiterName: string;
  aiRecruiterName: string;
  scheduleDateTs?: string;
  interviewType?: InterviewType;
  interviewTypeDisplay: string;
  jobTitle: string;
}
